import React, { useEffect, useState, useMemo } from 'react';
import { Button } from '../components/ui/button';
import { ProductCard } from '../components/custom/ProductCard';
import { ReelShowcaseCarousel } from '../components/custom/ReelShowcase';
import api from '../utils/api';
import { useNavigate } from 'react-router-dom';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { 
  Truck, ShieldCheck, MessageCircle, Star, Clock, Sparkles, 
  ChevronLeft, ChevronRight, Quote, Baby, GraduationCap, Backpack, Flame
} from 'lucide-react';

// Helper function to check if product age range overlaps with target range
const ageRangeOverlaps = (productAgeRange, targetMin, targetMax) => {
  if (!productAgeRange) return true; // If no age range, show everywhere
  
  // Parse product age range (e.g., "3-6", "3-12", "6-9", "All Ages")
  if (productAgeRange === "All Ages") return true;
  
  const match = productAgeRange.match(/(\d+)-(\d+)/);
  if (!match) return true;
  
  const productMin = parseInt(match[1]);
  const productMax = parseInt(match[2]);
  
  // Check if ranges overlap
  return productMin <= targetMax && productMax >= targetMin;
};

// Trending Products Section - Shows immediately after hero
const TrendingNow = ({ products, loading }) => {
  const navigate = useNavigate();
  
  // Get top 6 products for trending
  const trendingProducts = products.slice(0, 6);
  
  if (loading) {
    return (
      <div className="py-6">
        <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
          <Flame className="w-5 h-5 text-orange-500" />
          Trending Now
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {[...Array(6)].map((_, i) => (
            <Skeleton key={i} className="h-64 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }
  
  if (trendingProducts.length === 0) return null;
  
  return (
    <div className="py-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold flex items-center gap-2">
          <Flame className="w-5 h-5 text-orange-500" />
          Trending Now
        </h2>
        <Button variant="ghost" size="sm" onClick={() => navigate('/products')}>
          View All
        </Button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {trendingProducts.map((product, idx) => (
          <ProductCard key={product.id} product={product} showBestseller={idx < 2} />
        ))}
      </div>
    </div>
  );
};

// Product of the Day Component
const ProductOfTheDay = ({ products }) => {
  const todaysProduct = useMemo(() => {
    if (!products || products.length === 0) return null;
    // Use date to deterministically pick a product (changes daily)
    const today = new Date();
    const dayOfYear = Math.floor((today - new Date(today.getFullYear(), 0, 0)) / (1000 * 60 * 60 * 24));
    return products[dayOfYear % products.length];
  }, [products]);

  if (!todaysProduct) return null;

  return (
    <div className="relative bg-gradient-to-r from-amber-50 via-orange-50 to-yellow-50 rounded-2xl p-6 border border-amber-200 overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-yellow-300/30 to-orange-300/30 rounded-full -translate-y-1/2 translate-x-1/2" />
      <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-amber-300/20 to-yellow-300/20 rounded-full translate-y-1/2 -translate-x-1/2" />
      
      <div className="relative flex flex-col sm:flex-row gap-6 items-center">
        {/* Badge */}
        <div className="absolute -top-2 -left-2 sm:top-0 sm:left-0">
          <Badge className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-3 py-1 text-sm font-semibold shadow-lg">
            <Sparkles className="w-4 h-4 mr-1" />
            Product of the Day
          </Badge>
        </div>
        
        {/* Product Image */}
        <div className="w-40 h-40 sm:w-48 sm:h-48 flex-shrink-0 mt-6 sm:mt-0">
          <img 
            src={todaysProduct.image_urls?.[0]} 
            alt={todaysProduct.title}
            className="w-full h-full object-cover rounded-xl shadow-lg"
          />
        </div>
        
        {/* Product Info */}
        <div className="flex-1 text-center sm:text-left">
          <h3 className="text-xl sm:text-2xl font-bold text-gray-800 mb-2">{todaysProduct.title}</h3>
          <p className="text-gray-600 text-sm mb-3 line-clamp-2">{todaysProduct.description}</p>
          <div className="flex items-center justify-center sm:justify-start gap-3 mb-4">
            <span className="text-2xl font-extrabold text-orange-600">₹{todaysProduct.price}</span>
            {todaysProduct.original_price && (
              <span className="text-lg text-gray-400 line-through">₹{todaysProduct.original_price}</span>
            )}
          </div>
          <Button 
            className="rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
            onClick={() => window.location.href = `/products/${todaysProduct.id}`}
          >
            Shop Now
          </Button>
        </div>
      </div>
    </div>
  );
};

// Flash Deal with Countdown Component
const FlashDeal = ({ products }) => {
  const [timeLeft, setTimeLeft] = useState({ hours: 0, minutes: 0, seconds: 0 });
  
  const dealProduct = useMemo(() => {
    if (!products || products.length === 0) return null;
    // Pick a product with discount
    const discountedProducts = products.filter(p => p.original_price && p.original_price > p.price);
    if (discountedProducts.length === 0) return products[0];
    // Rotate based on hour
    const hour = new Date().getHours();
    return discountedProducts[hour % discountedProducts.length];
  }, [products]);

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date();
      const endOfDay = new Date(now);
      endOfDay.setHours(23, 59, 59, 999);
      const diff = endOfDay - now;
      
      return {
        hours: Math.floor(diff / (1000 * 60 * 60)),
        minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((diff % (1000 * 60)) / 1000)
      };
    };

    setTimeLeft(calculateTimeLeft());
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  if (!dealProduct) return null;

  const discount = dealProduct.original_price 
    ? Math.round((1 - dealProduct.price / dealProduct.original_price) * 100) 
    : 0;

  return (
    <div className="bg-gradient-to-r from-red-500 via-pink-500 to-rose-500 rounded-2xl p-6 text-white overflow-hidden relative">
      {/* Animated background */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-0 left-1/4 w-64 h-64 bg-white rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-0 right-1/4 w-48 h-48 bg-yellow-300 rounded-full blur-3xl animate-pulse delay-500" />
      </div>
      
      <div className="relative">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-2">
            <Clock className="w-6 h-6" />
            <h3 className="text-xl sm:text-2xl font-bold">Flash Deal - Ends Tonight!</h3>
          </div>
          
          {/* Countdown */}
          <div className="flex gap-2">
            {[
              { value: timeLeft.hours, label: 'HRS' },
              { value: timeLeft.minutes, label: 'MIN' },
              { value: timeLeft.seconds, label: 'SEC' }
            ].map((item, idx) => (
              <div key={idx} className="bg-white/20 backdrop-blur rounded-lg px-3 py-2 text-center min-w-[60px]">
                <div className="text-2xl font-bold">{String(item.value).padStart(2, '0')}</div>
                <div className="text-xs opacity-80">{item.label}</div>
              </div>
            ))}
          </div>
        </div>
        
        {/* Product */}
        <div className="flex flex-col sm:flex-row gap-6 items-center">
          <div className="relative">
            <img 
              src={dealProduct.image_urls?.[0]} 
              alt={dealProduct.title}
              className="w-36 h-36 sm:w-44 sm:h-44 object-cover rounded-xl shadow-xl"
            />
            {discount > 0 && (
              <div className="absolute -top-2 -right-2 bg-yellow-400 text-gray-900 font-bold px-3 py-1 rounded-full text-sm shadow-lg">
                -{discount}%
              </div>
            )}
          </div>
          
          <div className="flex-1 text-center sm:text-left">
            <h4 className="text-lg sm:text-xl font-semibold mb-2">{dealProduct.title}</h4>
            <div className="flex items-center justify-center sm:justify-start gap-3 mb-4">
              <span className="text-3xl font-extrabold">₹{dealProduct.price}</span>
              {dealProduct.original_price && (
                <span className="text-xl opacity-60 line-through">₹{dealProduct.original_price}</span>
              )}
            </div>
            <Button 
              className="rounded-xl bg-white text-pink-600 hover:bg-gray-100 font-semibold"
              onClick={() => window.location.href = `/products/${dealProduct.id}`}
            >
              Grab This Deal
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Shop by Age Component
const ShopByAge = ({ products }) => {
  const ageGroups = [
    { min: 3, max: 6, label: '3-6 Years', icon: Baby, color: 'from-pink-400 to-rose-400', bgColor: 'bg-pink-50' },
    { min: 6, max: 9, label: '6-9 Years', icon: Backpack, color: 'from-blue-400 to-cyan-400', bgColor: 'bg-blue-50' },
    { min: 9, max: 12, label: '9-12 Years', icon: GraduationCap, color: 'from-purple-400 to-indigo-400', bgColor: 'bg-purple-50' },
  ];

  const getProductsForAge = (min, max) => {
    return products.filter(p => ageRangeOverlaps(p.age_range, min, max)).slice(0, 4);
  };

  const navigate = useNavigate();

  return (
    <div className="space-y-8">
      <div className="text-center">
        <h2 className="text-2xl sm:text-3xl font-bold mb-2">👶 Shop by Age</h2>
        <p className="text-muted-foreground">Find the perfect fit for your little one</p>
      </div>
      
      <div className="grid md:grid-cols-3 gap-6">
        {ageGroups.map((group) => {
          const groupProducts = getProductsForAge(group.min, group.max);
          const Icon = group.icon;
          
          return (
            <Card key={group.label} className={`overflow-hidden ${group.bgColor} border-0 shadow-lg`}>
              <CardContent className="p-4">
                {/* Header */}
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-12 h-12 rounded-full bg-gradient-to-r ${group.color} flex items-center justify-center shadow-md`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg">{group.label}</h3>
                    <p className="text-xs text-muted-foreground">{groupProducts.length}+ products</p>
                  </div>
                </div>
                
                {/* Product Grid Preview */}
                <div className="grid grid-cols-2 gap-2 mb-4">
                  {groupProducts.slice(0, 4).map((product, idx) => (
                    <div 
                      key={product.id} 
                      className="aspect-square rounded-lg overflow-hidden cursor-pointer hover:scale-105 transition-transform"
                      onClick={() => navigate(`/products/${product.id}`)}
                    >
                      <img 
                        src={product.image_urls?.[0]} 
                        alt={product.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ))}
                </div>
                
                {/* View All Button */}
                <Button 
                  variant="outline" 
                  className="w-full rounded-xl"
                  onClick={() => navigate(`/products?age=${group.min}-${group.max}`)}
                >
                  View All {group.label}
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

// Happy Parents Reviews Component
const HappyParentsReviews = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  
  const reviews = [
    {
      name: "Priya Sharma",
      location: "Mumbai",
      rating: 5,
      review: "My daughter loves her unicorn lunchbox! It keeps food warm for hours and the leak-proof design is a lifesaver. Highly recommend!",
      avatar: "👩"
    },
    {
      name: "Rahul Verma",
      location: "Delhi",
      rating: 5,
      review: "Bought the dinosaur backpack for my son. The quality is amazing and it's super comfortable for him. Great customer service too!",
      avatar: "👨"
    },
    {
      name: "Anita Patel",
      location: "Bangalore",
      rating: 5,
      review: "Best lunchbox we've ever bought! Easy to clean, perfect size, and my kids actually finish their food now. Thank you Tiffin & Bag!",
      avatar: "👩"
    },
    {
      name: "Vikram Singh",
      location: "Hyderabad",
      rating: 5,
      review: "Fast delivery and the bag quality exceeded my expectations. My daughter shows it off to all her friends!",
      avatar: "👨"
    },
    {
      name: "Sneha Reddy",
      location: "Chennai",
      rating: 5,
      review: "Ordered a combo set - both the bag and lunchbox are adorable! The free delivery was a nice bonus. Will order again!",
      avatar: "👩"
    }
  ];

  const nextReview = () => {
    setCurrentIndex((prev) => (prev + 1) % reviews.length);
  };

  const prevReview = () => {
    setCurrentIndex((prev) => (prev - 1 + reviews.length) % reviews.length);
  };

  // Auto-scroll
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % reviews.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [reviews.length]);

  return (
    <div className="bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 rounded-2xl p-6 sm:p-8">
      <div className="text-center mb-6">
        <h2 className="text-2xl sm:text-3xl font-bold mb-2">💬 Happy Parents</h2>
        <p className="text-muted-foreground">See what other parents are saying</p>
      </div>
      
      <div className="relative max-w-2xl mx-auto">
        {/* Navigation Buttons */}
        <button 
          onClick={prevReview}
          className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 sm:-translate-x-4 z-10 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-50 transition-colors"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <button 
          onClick={nextReview}
          className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 sm:translate-x-4 z-10 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-50 transition-colors"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
        
        {/* Review Card */}
        <Card className="bg-white shadow-xl border-0">
          <CardContent className="p-6 sm:p-8">
            <Quote className="w-10 h-10 text-green-200 mb-4" />
            
            <p className="text-gray-700 text-lg mb-6 italic leading-relaxed">
              "{reviews[currentIndex].review}"
            </p>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-green-400 to-emerald-400 rounded-full flex items-center justify-center text-2xl">
                  {reviews[currentIndex].avatar}
                </div>
                <div>
                  <p className="font-semibold">{reviews[currentIndex].name}</p>
                  <p className="text-sm text-muted-foreground">{reviews[currentIndex].location}</p>
                </div>
              </div>
              
              <div className="flex gap-0.5">
                {[...Array(reviews[currentIndex].rating)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Dots */}
        <div className="flex justify-center gap-2 mt-4">
          {reviews.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentIndex(idx)}
              className={`w-2 h-2 rounded-full transition-colors ${
                idx === currentIndex ? 'bg-green-500' : 'bg-gray-300'
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

function HomePage() {
  const [reels, setReels] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [lunchboxes, setLunchboxes] = useState([]);
  const [bags, setBags] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [reelsData, lunchboxData, bagsData] = await Promise.all([
          api.getReels(),
          api.getProducts('lunchbox'),
          api.getProducts('bag'),
        ]);
        setReels(reelsData.reels || []);
        const lunchboxProducts = lunchboxData.products || [];
        const bagProducts = bagsData.products || [];
        setLunchboxes(lunchboxProducts.slice(0, 4));
        setBags(bagProducts.slice(0, 4));
        setAllProducts([...lunchboxProducts, ...bagProducts]);
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  return (
    <div className="min-h-screen" data-testid="homepage">
      {/* Hero Section */}
      <section className="relative overflow-hidden py-12 sm:py-20 px-4">
        <div 
          className="absolute inset-0 -z-10"
          style={{
            background: `radial-gradient(900px circle at 15% 10%, rgba(29,180,214,0.16), transparent 55%), 
                        radial-gradient(700px circle at 90% 5%, rgba(251,191,36,0.18), transparent 52%), 
                        radial-gradient(700px circle at 70% 95%, rgba(52,211,153,0.14), transparent 48%)`
          }}
        />
        
        <div className="container mx-auto max-w-6xl">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div className="space-y-6 text-center lg:text-left">
              <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-normal tracking-tight text-balance">
                Fun Lunch Boxes & Cool School Bags for Kids!
              </h1>
              <p className="text-lg text-muted-foreground max-w-xl">
                Leak-proof lunchboxes and comfy school bags your kids will love. 
                Free delivery on every order!
              </p>
              <div className="flex flex-wrap gap-3 justify-center lg:justify-start">
                <Button 
                  size="lg" 
                  className="rounded-xl text-base"
                  onClick={() => navigate('/products?category=lunchbox')}
                  data-testid="hero-shop-lunchboxes"
                >
                  Shop Lunchboxes
                </Button>
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="rounded-xl text-base"
                  onClick={() => navigate('/products?category=bag')}
                  data-testid="hero-shop-bags"
                >
                  Shop School Bags
                </Button>
              </div>
            </div>
            <div className="flex justify-center">
              <img
                src="https://customer-assets.emergentagent.com/job_66b3b77f-c15b-45b7-aed3-10783eb10817/artifacts/utu524nd_IMG-20260321-WA0015.jpg"
                alt="Colorful kids lunchboxes and school bags - Playful Adventures Await"
                className="rounded-2xl shadow-xl max-w-md w-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Trending Now - RIGHT AFTER HERO (Products visible immediately) */}
      <section className="py-6 sm:py-8 px-4 bg-gradient-to-b from-white to-gray-50">
        <div className="container mx-auto max-w-6xl">
          <TrendingNow products={allProducts} loading={loading} />
        </div>
      </section>

      {/* Flash Deal Section - Now below trending */}
      {!loading && allProducts.length > 0 && (
        <section className="py-6 sm:py-8 px-4">
          <div className="container mx-auto max-w-6xl">
            <FlashDeal products={allProducts} />
          </div>
        </section>
      )}

      {/* Product of the Day - Compact */}
      {!loading && allProducts.length > 0 && (
        <section className="py-6 sm:py-8 px-4 bg-muted/30">
          <div className="container mx-auto max-w-6xl">
            <ProductOfTheDay products={allProducts} />
          </div>
        </section>
      )}

      {/* Reels Showcase */}
      {reels.length > 0 && (
        <section className="py-10 sm:py-14 px-4">
          <div className="container mx-auto max-w-6xl">
            <h2 className="text-2xl font-semibold mb-6 text-center">Watch & Shop 🎥</h2>
            <ReelShowcaseCarousel reels={reels} />
          </div>
        </section>
      )}

      {/* Shop by Age Section */}
      {!loading && allProducts.length > 0 && (
        <section className="py-10 sm:py-14 px-4 bg-muted/30">
          <div className="container mx-auto max-w-6xl">
            <ShopByAge products={allProducts} />
          </div>
        </section>
      )}

      {/* Featured Lunchboxes */}
      <section className="py-8 sm:py-10 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-semibold">🍱 Lunchboxes</h2>
            <Button 
              variant="ghost" 
              onClick={() => navigate('/products?category=lunchbox')}
            >
              View All
            </Button>
          </div>
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[...Array(4)].map((_, i) => (
                <Skeleton key={i} className="h-80 rounded-xl" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
              {lunchboxes.map((product, idx) => (
                <ProductCard key={product.id} product={product} showBestseller={idx === 0} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Featured Bags */}
      <section className="py-8 sm:py-10 px-4 bg-muted/50">
        <div className="container mx-auto max-w-6xl">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-semibold">🎒 School Bags</h2>
            <Button 
              variant="ghost" 
              onClick={() => navigate('/products?category=bag')}
            >
              View All
            </Button>
          </div>
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[...Array(4)].map((_, i) => (
                <Skeleton key={i} className="h-80 rounded-xl" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
              {bags.map((product, idx) => (
                <ProductCard key={product.id} product={product} showBestseller={idx === 0} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Happy Parents Reviews */}
      <section className="py-10 sm:py-14 px-4">
        <div className="container mx-auto max-w-6xl">
          <HappyParentsReviews />
        </div>
      </section>

      {/* Trust Strip */}
      <section className="py-10 sm:py-14 px-4 border-t">
        <div className="container mx-auto max-w-6xl">
          <div className="grid sm:grid-cols-3 gap-8 text-center">
            <div className="flex flex-col items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-[hsl(var(--secondary))] flex items-center justify-center">
                <Truck className="w-6 h-6 text-[hsl(var(--primary))]" />
              </div>
              <h3 className="font-semibold">Free Delivery</h3>
              <p className="text-sm text-muted-foreground">On all orders, no minimum</p>
            </div>
            <div className="flex flex-col items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-[hsl(var(--secondary))] flex items-center justify-center">
                <ShieldCheck className="w-6 h-6 text-[hsl(var(--primary))]" />
              </div>
              <h3 className="font-semibold">Secure Payment</h3>
              <p className="text-sm text-muted-foreground">Safe & encrypted checkout</p>
            </div>
            <div className="flex flex-col items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-[hsl(var(--secondary))] flex items-center justify-center">
                <MessageCircle className="w-6 h-6 text-[hsl(var(--primary))]" />
              </div>
              <h3 className="font-semibold">WhatsApp Support</h3>
              <p className="text-sm text-muted-foreground">Quick help via WhatsApp</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default HomePage;
